import { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Flex, Tooltip } from 'antd';
import type {
  ResolveAlertActionData,
  TextTemplateChunk,
  VariableRef,
} from '@/proto/logiblocks/logiblocks';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { AddSelectableItem, CustomRadioGroup } from '@/components';
import { TemplatedTextArea } from '@/pages/Logiblocks/variables/TemplatedTextArea/TemplatedTextArea';
import { UserMentionSource } from '@/pages/Logiblocks/variables/TemplatedTextArea/mentionList';
import './ResolveAlertActionCustomization.css';
import { BrackedAddIcon } from '@/assets/icons/icons';
import { useFetchAlertThresholds } from '@/hooks';
import ShipmentAlertThresholds from '@/pages/Shipments/PerShipmentNotification/components/ShipmentAlertThresholds';
import type { SelectedThresholdValues } from '@/pages/Shipments/PerShipmentNotification/components/AlertFieldByType';
import { FieldErrorLabel, ShipmentField } from '../shared';
import { ResolveAlertActionFields } from './ResolveAlertActionNodeInfo';

export const ResolveAlertActionCustomization: CustomizationComponent = ({
  nodeData,
  onChange,
  isSidePannelOpen,
  nodeId,
  step,
  setStep,
  childAddSelectedTypesRef,
  isReadOnly = false,
  validationErrors,
}) => {
  const { alertThreshold, isLoading } = useFetchAlertThresholds(null);
  const [selectAllAlertTypes, setSelectAllAlertTypes] = useState(false);
  const [selectedThresholdValues, setSelectedThresholdValues] = useState({});
  const [form] = Form.useForm();
  const [updateAlertData, setUpdateAlertData] = useState(false);
  const [shipmentId, setShipmentId] = useState<VariableRef | undefined>(
    nodeData.resolve_alert_action?.shipment_id
  );
  const [selectedStatus, setSelectedStatus] = useState<string>(
    nodeData.resolve_alert_action?.alert_status || 'resolved'
  );

  const shipmentError = validationErrors?.find(
    (e) => e.field === ResolveAlertActionFields.SHIPMENT
  );
  const commentError = validationErrors?.find(
    (e) => e.field === ResolveAlertActionFields.COMMENT
  );

  // Reference to trigger variable picker programmatically
  const triggerPickerRef = useRef<(() => void) | null>(null);

  // Track the last initialized node ID to prevent re-initialization
  const lastInitializedNodeId = useRef<string | null>(null);

  // Initialize form with existing nodeData - only when node changes or panel opens
  useEffect(() => {
    // Only initialize if:
    // 1. Panel is open AND
    // 2. Either first time OR switching to different node
    if (isSidePannelOpen && lastInitializedNodeId.current !== nodeId) {
      const alertStatus =
        nodeData.resolve_alert_action?.alert_status || 'resolved';
      const alertTypes = nodeData.resolve_alert_action?.alert_types || [];
      const selectAll =
        nodeData.resolve_alert_action?.select_all_alert_types || false;

      // Update local state
      setShipmentId(nodeData.resolve_alert_action?.shipment_id);
      setSelectedStatus(alertStatus);
      setSelectAllAlertTypes(selectAll);

      // Reconstruct selectedThresholdValues from alertTypes
      if (alertTypes.length > 0) {
        const thresholdValues: SelectedThresholdValues = {};
        alertTypes.forEach((type: string) => {
          thresholdValues[type] = '';
        });
        setSelectedThresholdValues(thresholdValues);
      }

      // Update form fields
      form.setFieldsValue({
        alert_status: alertStatus,
      });

      // Mark this node as initialized
      lastInitializedNodeId.current = nodeId;
    }
  }, [
    isSidePannelOpen,
    nodeId,
    nodeData.resolve_alert_action?.shipment_id,
    nodeData.resolve_alert_action?.alert_status,
    nodeData.resolve_alert_action?.alert_types,
    nodeData.resolve_alert_action?.select_all_alert_types,
    form,
  ]);

  const handleShipmentIdChange = (ref: VariableRef | null) => {
    const value = ref ?? undefined;
    setShipmentId(value);
    onChange({
      resolve_alert_action: {
        ...nodeData.resolve_alert_action,
        shipment_id: value,
      },
    });
  };

  // Update nodeData whenever status changes
  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    onChange({
      resolve_alert_action: {
        ...nodeData.resolve_alert_action,
        alert_status: value,
      },
    });
  };

  const handleAddSelectedTypes = useCallback(() => {
    let keys: string[] = [];
    if (Object.keys(selectedThresholdValues)?.length > 0) {
      keys = Object.keys(selectedThresholdValues);
    }
    const resolveAlertAction: ResolveAlertActionData = {
      ...nodeData.resolve_alert_action,
      alert_types: keys,
      select_all_alert_types: selectAllAlertTypes,
    };
    onChange({ resolve_alert_action: resolveAlertAction });
    setStep(2);
  }, [
    selectedThresholdValues,
    nodeData.resolve_alert_action,
    onChange,
    setStep,
    selectAllAlertTypes,
  ]);

  // Set up the ref for the parent to call handleAddSelectedTypes
  useEffect(() => {
    if (childAddSelectedTypesRef) {
      childAddSelectedTypesRef.current = handleAddSelectedTypes;
    }
  }, [childAddSelectedTypesRef, handleAddSelectedTypes]);

  /** Writes an edited comment as chunks. */
  const handleCommentChange = (chunks: TextTemplateChunk[]) => {
    onChange({
      resolve_alert_action: {
        ...nodeData.resolve_alert_action,
        comment_chunks: chunks,
      },
    });
  };

  // When delete is triggered at step 2, persist the updated selection immediately
  useEffect(() => {
    if (updateAlertData) {
      handleAddSelectedTypes();
      setUpdateAlertData(false);
    }
  }, [updateAlertData, handleAddSelectedTypes]);

  // Handle {+} icon click to open variable picker
  const handleVariablesIconClick = () => {
    triggerPickerRef.current?.();
  };

  return (
    <Form form={form} layout='vertical'>
      <Flex vertical gap={16}>
        <ShipmentField
          nodeId={nodeId}
          value={shipmentId}
          onChange={handleShipmentIdChange}
          disabled={isReadOnly}
          error={shipmentError}
        />

        {/*Select Alert type*/}
        <Flex vertical>
          <ShipmentAlertThresholds
            shipment={null}
            selectAllAlertTypes={selectAllAlertTypes}
            setSelectAllAlertTypes={setSelectAllAlertTypes}
            step={step}
            alertThreshold={alertThreshold}
            setSelectedThresholdValues={setSelectedThresholdValues}
            selectedThresholdValues={selectedThresholdValues}
            maxHeight={'100%'}
            showThresholdInputSection={false}
            enableAllThresholds={true}
            isLoading={isLoading}
            setUpdateAlertData={setUpdateAlertData}
            disabled={isReadOnly}
          />

          {step === 2 && (
            <>
              <AddSelectableItem
                text='Select alert'
                onClick={() => setStep(3)}
              />
            </>
          )}
        </Flex>

        {step === 2 && (
          <>
            {/* Select Alert Status */}
            <Form.Item
              label={'Set status to'}
              name='alert_status'
              rules={[{ required: true, message: 'Please choose one option' }]}
              style={{ marginBottom: '10px' }}
            >
              <div className='resolve-alert-radio-group'>
                <CustomRadioGroup
                  value={selectedStatus}
                  horizontal={false}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    handleStatusChange(e.target.value);
                  }}
                  options={[
                    {
                      label: 'Resolved',
                      value: 'resolved',
                    },
                    {
                      label: 'Dismissed',
                      value: 'dismissed',
                    },
                  ]}
                />
              </div>
            </Form.Item>

            {/* Comment Box */}
            <Form.Item
              label={
                <>
                  <Flex align='center' gap={8}>
                    <span>Comment</span>
                    {!isReadOnly && (
                      <Tooltip
                        title='Insert variable ({)'
                        styles={{
                          container: {
                            fontSize: '10px',
                          },
                        }}
                      >
                        <Flex onClick={handleVariablesIconClick}>
                          <BrackedAddIcon />
                        </Flex>
                      </Tooltip>
                    )}
                  </Flex>
                </>
              }
              style={{ marginBottom: '0' }}
            >
              <TemplatedTextArea
                nodeId={nodeId}
                chunks={nodeData.resolve_alert_action?.comment_chunks ?? []}
                richText
                userMentionSource={UserMentionSource.COMMENT_MENTIONABLES}
                onChange={handleCommentChange}
                placeholder='Add comment or mention users... Use @ for mentions, { for variables'
                onTriggerPickerRef={(fn) => {
                  triggerPickerRef.current = fn;
                }}
                pickerAsModal
                disabled={isReadOnly}
              />
              <FieldErrorLabel error={commentError} />
            </Form.Item>
          </>
        )}
      </Flex>
    </Form>
  );
};
